import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@application/config';
import {
  TREASURY_SYSTEM_PERSON_DISPLAY_NAME,
  isTreasurySystemPersonId,
} from '@domains/treasury/util';
import { merge, Observable, of } from 'rxjs';
import { map, catchError, shareReplay, toArray } from 'rxjs/operators';

/**
 * Lightweight service that resolves PersonId → FullName with in-memory caching.
 * Batches concurrent requests to avoid N+1 calls when many sessions share
 * different operators. Used primarily by the treasury module to render the
 * `OpenedBy` column on cash-session cards.
 */
@Injectable({ providedIn: 'root' })
export class PersonLookupService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl as Record<
    string,
    string | undefined
  >;

  /** LRU-style cache — kept simple: plain Map with bounded size. */
  private readonly cache = new Map<string, string>();

  /** In-flight batch requests keyed by PersonId to dedupe concurrent calls. */
  private readonly pending = new Map<string, Observable<string | null>>();

  private get personBaseUrl(): string {
    return this.apiUrl?.['personService'] ?? '';
  }

  /**
   * Resolve a single PersonId to its FullName. Returns `null` when the
   * lookup fails or the person is not found. Cached after first fetch.
   */
  public resolveName(personId: string): Observable<string | null> {
    if (!personId) {
      return of(null);
    }

    if (isTreasurySystemPersonId(personId)) {
      this.cache.set(personId, TREASURY_SYSTEM_PERSON_DISPLAY_NAME);
      return of(TREASURY_SYSTEM_PERSON_DISPLAY_NAME);
    }

    if (this.cache.has(personId)) {
      return of(this.cache.get(personId) ?? null);
    }

    if (this.pending.has(personId)) {
      return this.pending.get(personId)!;
    }

    const request$ = this.http
      .get<unknown>(`${this.personBaseUrl}/Person`, {
        params: { id: personId },
        withCredentials: true,
      })
      .pipe(
        map((response: unknown) => {
          const record =
            response && typeof response === 'object'
              ? (response as Record<string, unknown>)
              : null;
          const collection = record?.['value'] ?? record?.['Value'] ?? null;
          if (Array.isArray(collection) && collection.length > 0) {
            const entity = collection[0] as Record<string, unknown>;
            return (typeof entity['FullName'] === 'string'
              ? entity['FullName']
              : null) as string | null;
          }

          if (record && typeof record['FullName'] === 'string') {
            return record['FullName'] as string;
          }

          return null;
        }),
        catchError(() => of(null)),
        shareReplay(1)
      ) as Observable<string | null>;

    this.pending.set(personId, request$);

    request$.subscribe({
      next: (name) => {
        if (name) {
          this.cache.set(personId, name);
        }
        this.pending.delete(personId);
      },
      error: () => {
        this.pending.delete(personId);
      },
    });

    return request$;
  }

  /**
   * Resolve a batch of unique PersonIds in parallel; each lookup completes independently.
   * Returns a map of `PersonId → FullName | null` when all misses have resolved.
   */
  public resolveNames(personIds: string[]): Observable<Map<string, string | null>> {
    const uniqueIds = [...new Set(personIds)].filter(Boolean);
    if (uniqueIds.length === 0) {
      return of(new Map<string, string | null>());
    }

    // Split into cache hits and misses
    const hits = new Map<string, string | null>();
    const misses: string[] = [];

    for (const id of uniqueIds) {
      if (isTreasurySystemPersonId(id)) {
        this.cache.set(id, TREASURY_SYSTEM_PERSON_DISPLAY_NAME);
        hits.set(id, TREASURY_SYSTEM_PERSON_DISPLAY_NAME);
        continue;
      }
      if (this.cache.has(id)) {
        hits.set(id, this.cache.get(id) ?? null);
      } else {
        misses.push(id);
      }
    }

    if (misses.length === 0) {
      return of(hits);
    }

    return merge(
      ...misses.map((id) =>
        this.http
          .get<unknown>(`${this.personBaseUrl}/Person`, {
            params: { id },
            withCredentials: true,
          })
          .pipe(
            map((response: unknown) => {
              const record =
                response && typeof response === 'object'
                  ? (response as Record<string, unknown>)
                  : null;
              const collection = record?.['value'] ?? record?.['Value'] ?? null;
              if (Array.isArray(collection) && collection.length > 0) {
                const entity = collection[0] as Record<string, unknown>;
                return (typeof entity['FullName'] === 'string'
                  ? entity['FullName']
                  : null) as string | null;
              }

              if (record && typeof record['FullName'] === 'string') {
                return record['FullName'] as string;
              }

              return null;
            }),
            catchError(() => of(null)),
            map((name) => ({ id, name } as const))
          )
      )
    ).pipe(
      toArray(),
      map((pairs) => {
        const resolved = new Map<string, string | null>();

        for (const [id, name] of hits.entries()) {
          resolved.set(id, name);
        }

        for (const { id, name } of pairs) {
          if (name) {
            this.cache.set(id, name);
          }
          resolved.set(id, name);
        }

        return resolved;
      })
    );
  }

  /** Pre-populate the cache (e.g., from existing session OpenedBy IDs). */
  public seed(names: Map<string, string>): void {
    for (const [id, name] of names.entries()) {
      if (name) {
        this.cache.set(id, name);
      }
    }
  }
}
